import { useToastError } from "@/components/ui/use-toast";
import { useApiStream, type SSEError } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { companyRefusalKey } from "@/lib/company-refusal";
import {
  companyPageStore,
  INITIAL_COMPANY_PAGE_STATE,
  type CompanyPageState,
} from "@/stores/company-page-store";
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/** No company to read: nothing will arrive, the page shows its error state. */
const NO_COMPANY_STATE: CompanyPageState = {
  ...INITIAL_COMPANY_PAGE_STATE,
  done: true,
};

/**
 * The company page as its stream sends it (`companyPageStore`): the snapshot,
 * then each section, then `done`, for the team the requests are sent for.
 * Mounting starts the company's run — or joins the one in progress, so two
 * views of one company share one stream — and holds it: leaving the page, or
 * moving to another company or team, releases it, and the last view to go
 * aborts the stream. A page whose entry the store evicted starts it again.
 */
export function useCompanyPage(companyId: string): CompanyPageState {
  const { get, teamId } = useApiStream();
  const state = useSyncExternalStore(companyPageStore.subscribe, () =>
    companyPageStore.getState(teamId, companyId),
  );
  const isMissing = state === undefined;
  const visibleState = useWithoutLeftOverRun(`${teamId}|${companyId}`, state);

  // `get` is a new function on each render: read the latest one, so the run
  // starts again on a new company or team only.
  const startRun = useEffectEvent((team: string, id: string) => {
    void companyPageStore.start(team, id, get);
  });

  // Retained before it starts: under StrictMode's mount, unmount and mount,
  // the release ends the first run and the second mount opens a new one.
  useEffect(() => {
    if (!companyId) return;
    const release = companyPageStore.retain(teamId, companyId);
    startRun(teamId, companyId);
    return release;
  }, [teamId, companyId]);

  useEffect(() => {
    if (companyId && isMissing) startRun(teamId, companyId);
  }, [teamId, companyId, isMissing]);

  if (!companyId) return NO_COMPANY_STATE;
  return visibleState ?? INITIAL_COMPANY_PAGE_STATE;
}

/**
 * The entry without what an earlier visit left in it. The entry as this visit
 * first read it, when finished, is not this visit's run: until the run the
 * page starts replaces it, the page is not done — the cards waiting on `done`
 * (the AI advice) run their effects before the page's and would read a stale
 * sheet.
 */
function useWithoutLeftOverRun(
  runKey: string,
  state: CompanyPageState | undefined,
): CompanyPageState | undefined {
  const [entryAtMount, setEntryAtMount] = useState({ runKey, state });
  if (entryAtMount.runKey !== runKey) setEntryAtMount({ runKey, state });
  const isLeftOver =
    state !== undefined &&
    state.done &&
    entryAtMount.runKey === runKey &&
    state === entryAtMount.state;
  return useMemo(
    // Its sections too: they are the earlier run's, and would say nothing
    // runs before this run's snapshot tells which sections it enriches. And
    // its error, which the page's error toast would show a second time.
    () =>
      isLeftOver && state
        ? { ...state, done: false, error: null, sections: {} }
        : state,
    [isLeftOver, state],
  );
}

/**
 * Toasts a run that failed while the page showed the company — a refusal (a
 * throttle, the licence, the team) with its own message. A company that never
 * arrived shows the error in its place, and is not toasted too. The error a
 * finished run left in the store before the page mounted is not toasted
 * again: the run the page starts clears it.
 */
export function useCompanyPageErrorToast(
  error: SSEError | null,
  isCompanyShown: boolean,
): void {
  const { t } = useTranslation();
  const toastError = useToastError();
  const shownRef = useRef(error);

  useEffect(() => {
    if (!error || error === shownRef.current) return;
    shownRef.current = error;
    if (!isCompanyShown) return;
    const refusal = companyRefusalKey(error);
    toastError(refusal ? { description: t(refusal) } : undefined);
  }, [error, isCompanyShown, toastError, t]);
}
